import { computed, defineComponent, PropType, ref, watch } from '@vue/composition-api';
import {
  createRule,
  displayValue,
  isRuleComplete,
  operatorsForType,
  parseTypedValue,
  scopedFields,
} from './helpers';
import {
  FieldDescriptor,
  FieldSource,
  JsonValue,
  RuleKind,
  RuleNode,
  ScalarType,
  fieldSources,
  ruleKinds,
  scalarTypes,
} from './types';

export default defineComponent({
  name: 'ImportRuleNode',
  props: {
    node: { type: Object as PropType<RuleNode>, required: true },
    fields: { type: Array as PropType<FieldDescriptor[]>, required: true },
    scope: { type: String, default: '' },
    scopeSource: { type: String as PropType<FieldSource>, default: null },
    labelId: { type: String, required: true },
    disabled: { type: Boolean, default: false },
  },
  setup(props, context) {
    const inputError = ref(false);
    const draftText = ref<string | null>(null);
    const childValidityStates = ref<boolean[]>([]);
    let nextChildKey = 0;
    const childKeys = ref((props.node.children || []).map(() => nextChildKey++));
    const emitValidity = (node = props.node) =>
      context.emit(
        'validity',
        isRuleComplete(node) &&
          !inputError.value &&
          childValidityStates.value.every((value) => value)
      );
    watch(
      () => props.node,
      () => emitValidity(),
      { deep: true, immediate: true }
    );
    const childValidity = (index: number, valid: boolean) => {
      const values = childValidityStates.value.slice();
      values[index] = valid;
      childValidityStates.value = values;
      emitValidity();
    };
    const availableFields = computed(() =>
      scopedFields(props.fields, props.scopeSource || props.node.source || 'Raw', props.scope)
    );
    const choices = computed(() =>
      availableFields.value.filter((field) =>
        props.node.kind === 'AnyElement'
          ? field.valueType === 'array'
          : field.valueType !== 'object' && field.valueType !== 'array'
      )
    );
    const selectedField = computed(() =>
      choices.value.find((field) => field.path === props.node.path)
    );
    const childScope = computed(() =>
      props.node.kind === 'AnyElement'
        ? [props.scope, props.node.path].filter(Boolean).join('.')
        : props.scope
    );
    const textValue = computed(() =>
      draftText.value !== null
        ? draftText.value
        : typeof props.node.value === 'string'
        ? props.node.value
        : JSON.stringify(props.node.value)
    );
    const ruleOperators = computed(() => operatorsForType(props.node.valueType || 'String'));
    const needsValue = computed(
      () => props.node.operator !== 'isMissing' && props.node.operator !== 'isPresent'
    );
    const update = (changes: Partial<RuleNode>) => {
      const node = { ...props.node, ...changes };
      const typeChanged = node.valueType !== props.node.valueType;
      const defaultValue =
        node.valueType === 'Boolean' ? false : node.valueType === 'Number' ? 0 : '';

      if (node.kind === 'Condition') {
        if (
          !node.operator ||
          !operatorsForType(node.valueType || 'String').includes(node.operator)
        ) {
          node.operator = 'equals';
        }

        if (typeChanged || node.value === undefined) {
          node.value = defaultValue;
        }

        if (node.operator === 'isMissing' || node.operator === 'isPresent') {
          node.value = undefined;
        } else if (node.operator === 'in' && !Array.isArray(node.value)) {
          node.value = [node.value];
        } else if (node.operator !== 'in' && Array.isArray(node.value)) {
          node.value = node.value[0] === undefined ? defaultValue : node.value[0];
        }
      }

      if (
        typeChanged ||
        changes.path !== undefined ||
        'value' in changes ||
        node.operator === 'isMissing' ||
        node.operator === 'isPresent'
      ) {
        inputError.value = false;
        draftText.value = null;
      }

      context.emit('input', node);
      emitValidity(node);
    };
    const changeKind = (event: Event) => {
      const kind = (event.target as HTMLSelectElement).value as RuleKind;
      if (
        (props.node.kind === 'All' || props.node.kind === 'Any') &&
        (kind === 'All' || kind === 'Any')
      ) {
        update({ kind });
        return;
      }

      inputError.value = false;
      draftText.value = null;
      childValidityStates.value = [];
      const node = createRule(kind);
      childKeys.value = (node.children || []).map(() => nextChildKey++);
      if (props.scopeSource && (node.kind === 'Condition' || node.kind === 'AnyElement')) {
        node.source = props.scopeSource;
      }
      context.emit('input', node);
      emitValidity(node);
    };
    const changeSource = (event: Event) =>
      update({ source: (event.target as HTMLSelectElement).value as FieldSource, path: '' });
    const changePath = (event: Event) => {
      const path = (event.target as HTMLSelectElement).value;
      const field = choices.value.find((item) => item.path === path);
      update({
        path,
        source: props.scopeSource || props.node.source,
        valueType:
          field && field.valueType !== 'object' && field.valueType !== 'array'
            ? ((field.valueType.charAt(0).toUpperCase() + field.valueType.slice(1)) as ScalarType)
            : props.node.valueType,
      });
    };
    const changeType = (event: Event) => {
      const type = (event.target as HTMLSelectElement).value as ScalarType;
      update({ valueType: type, value: type === 'Boolean' ? false : type === 'Number' ? 0 : '' });
    };
    const changeValue = (event: Event) => {
      const text = (event.target as HTMLInputElement).value;
      draftText.value = text;
      try {
        let value: JsonValue;
        if (props.node.operator === 'in') {
          const parsed = JSON.parse(text);
          if (!Array.isArray(parsed)) {
            throw new Error('array');
          }
          value = parsed.map((item) =>
            parseTypedValue(String(item), props.node.valueType || 'String')
          );
        } else {
          value = parseTypedValue(text, props.node.valueType || 'String');
        }
        update({ value });
      } catch {
        inputError.value = true;
      }
      emitValidity();
    };
    const childChange = (index: number, node: RuleNode) => {
      const children = (props.node.children || []).slice();
      children.splice(index, 1, node);
      update({ children });
    };
    const removeChild = (index: number) => {
      childValidityStates.value = childValidityStates.value.filter((_, item) => index !== item);
      childKeys.value = childKeys.value.filter((_, item) => index !== item);
      update({ children: (props.node.children || []).filter((_, item) => index !== item) });
    };
    const canAddChild = computed(
      () => props.node.kind !== 'AnyElement' || !props.node.children?.length
    );
    const addChild = () => {
      if (!canAddChild.value) {
        return;
      }

      const child = createRule(props.node.kind === 'AnyElement' ? 'All' : 'Condition');
      if (props.scopeSource && child.kind === 'Condition') {
        child.source = props.scopeSource;
      }

      childKeys.value = childKeys.value.concat(nextChildKey++);
      update({ children: (props.node.children || []).concat(child) });
    };
    return {
      availableFields,
      choices,
      selectedField,
      childScope,
      textValue,
      needsValue,
      inputError,
      childValidity,
      childKeys,
      fieldSources,
      ruleKinds,
      ruleOperators,
      scalarTypes,
      update,
      changeKind,
      changeSource,
      changePath,
      changeType,
      changeValue,
      childChange,
      removeChild,
      addChild,
      canAddChild,
      displayValue,
    };
  },
});
